import React from "react";
import { Container, Row, Col, Card, ListGroup, Button } from "react-bootstrap";
import switchgearImg from "../Assets/Image/subassewitch01.jpg";
import { useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import {
  FaCheckCircle,
  FaIndustry,
  FaBuilding,
  FaTools,
  FaWarehouse,
} from "react-icons/fa";
import fingercontact from "../Assets/Image/fingercontact.jpeg";
import { Helmet } from "react-helmet";

const SubAssembliesSwitchgear = () => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate("/contact");
  };
  return (
    <>
      <Helmet>
        <title>Sub Assemblies - Switchgear | Delta Engineering</title>
        <meta
          name="description"
          content="Precision-engineered switchgear sub-assemblies designed for efficient, safe, and reliable power distribution. Custom-built for industrial, commercial, and utility applications."
        />
        <meta
          name="keywords"
          content="Switchgear Sub Assemblies, Finger Contact Assembly, Power Distribution Assemblies, Modular Switchgear, Electrical Panels, Busbar Systems, IEC 61439, IS 3427, Medium Voltage Switchgear"
        />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content="Sub Assemblies - Switchgear" />
        <meta
          property="og:description"
          content="Engineered for reliability and efficiency, our switchgear sub-assemblies include precision finger contact assemblies and modular designs, ensuring safety and high performance in power systems."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.yourdomain.com/sub-assemblies-switchgear"
        />
        <meta
          property="og:image"
          content="https://www.yourdomain.com/images/sub-assemblies-switchgear.jpg"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Sub Assemblies - Switchgear | Delta Engineering"
        />
        <meta
          name="twitter:description"
          content="Switchgear sub-assemblies designed for modularity, safety, and efficiency. Ideal for industrial, commercial, and utility power distribution systems."
        />
        <meta
          name="twitter:image"
          content="https://www.yourdomain.com/images/sub-assemblies-switchgear.jpg"
        />
      </Helmet>

      <section className="productbanner1 d-flex align-items-center position-relative">
        <div className="overlay-layer"></div>
        <Container className="text-center position-relative z-2">
          <Row className="justify-content-center">
            <Col lg={8} className="hero-content text-center">
              <h2
                className="hero-title"
                data-aos="zoom-in"
                data-aos-delay="300"
              >
               <span className="blue-text"> Sub Assemblies -</span>{" "}
            <span className="white-text"> Switchgear</span>
              </h2>
              <p
                className="hero-subtitle"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                 Engineered solutions for efficient and reliable switchgear assembly
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="py-3" style={{ position: "relative" }}>
        <Button onClick={() => navigate("/ourproducts")} className="back-btn">
          <IoMdArrowRoundBack className="back-icon" />
          Back
        </Button>
        <Container>
          <Row className="text-center mb-4">
            <Col>
              <h3 className="fw-bold section-title">
                Sub Assemblies - Switchgear
                <span className="title-underline"></span>
              </h3>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={4} data-aos="fade-zoom-in" data-aos-delay="200">
              <img
                src={switchgearImg}
                alt="Switchgear Assembly"
                className="img-fluid rounded shadow"
                style={{
                  maxHeight: "300px",
                  width: "500px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
            </Col>
            <Col md={8} data-aos="fade-left">
              <p>
                Our switchgear sub-assemblies are precision-engineered to meet
                the stringent demands of power control and distribution systems.
                These assemblies are designed for use in various voltage ranges
                and configurations, ensuring modularity, safety, and high
                operational reliability.
              </p>
              <p>
                Built with high-quality components and robust construction, our
                switchgear assemblies offer enhanced protection against
                electrical faults, overloads, and short circuits. They are
                designed for easy integration into existing systems and are
                suitable for industrial, commercial, and utility applications.
              </p>
              <p>
                Engineered for optimal performance, these assemblies feature
                advanced insulation materials, high-speed switching mechanisms,
                and precision-engineered busbar systems to minimize energy
                losses and ensure stable operation under varying load
                conditions. The modular design allows for easy customization,
                expansion, and maintenance, making it adaptable to evolving
                operational requirements.
              </p>
            </Col>
            <Row className="align-items-center mt-5">
              <Col md={8} data-aos="fade-left">
                <h4 className="mb-3 fw-bold" style={{ color: "#0056f5" }}>
                  Finger Contact Assembly for Switchgear
                </h4>

                <p>
                  Finger Contact Assemblies are critical components in
                  switchgear systems, designed to maintain a secure and reliable
                  electrical connection under varying load conditions.
                  Manufactured from high-conductivity materials, these
                  assemblies ensure low contact resistance and efficient current
                  transfer, reducing power loss and heating. Their robust
                  construction and precision engineering provide excellent
                  mechanical strength, wear resistance, and durability, making
                  them ideal for high-current, high-performance switchgear
                  applications.
                </p>
                <p>
                  Widely used in medium and low voltage panels, circuit
                  breakers, and busbar trunking systems, these assemblies
                  guarantee consistent performance, long service life, and
                  enhanced operational safety.
                </p>
              </Col>
              <Col md={4} data-aos="fade-right">
                <img
                  src={fingercontact}
                  alt="Finger Contact Assembly for Switchgear"
                  className="img-fluid rounded shadow"
                  loading="lazy"
                  style={{
                    maxHeight: "350px",
                    width: "500px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
              </Col>
            </Row>
          </Row>

          <Row className="mb-5">
            <Col md={6} data-aos="fade-up">
              <Card className="shadow-sm">
                <Card.Body>
                  <Card.Title className="text-success">Key Features</Card.Title>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <FaCheckCircle className="me-2 text-success" />
                      Modular and custom-built designs
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <FaCheckCircle className="me-2 text-success" />
                      High mechanical and electrical endurance
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <FaCheckCircle className="me-2 text-success" />
                      Tested Products
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <FaCheckCircle className="me-2 text-success" />
                      Enhanced safety and fault protection
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} data-aos="fade-up" data-aos-delay="100">
              <Card className="shadow-sm">
                <Card.Body>
                  <Card.Title className="text-info">Applications</Card.Title>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <FaIndustry className="me-2 text-info" />
                      Industrial switchboards
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <FaBuilding className="me-2 text-info" />
                      Commercial electrical panels
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <FaTools className="me-2 text-info" />
                      Infrastructure & utility projects
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <FaWarehouse className="me-2 text-info" />
                      OEM equipment manufacturers
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-5">
            <Col data-aos="fade-up">
              <Card className="shadow-sm">
                <Card.Body>
                  <Card.Title className="text-danger">
                    Technical Specifications
                  </Card.Title>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <strong>Voltage Range:</strong> Up to 36kV
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Current Rating:</strong> Up to 4000A
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Protection:</strong> IP42 to IP65 (depending on
                      configuration)
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Mounting:</strong> Floor / Wall mounted
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Standards:</strong> IEC 61439, IS 3427
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col className="text-center" data-aos="zoom-in">
              <h5>Need Tailored Switchgear Assemblies?</h5>
              <p>
                We offer custom switchgear sub-assemblies to meet your exact
                technical and dimensional requirements.
              </p>
              <button
                className="btn btn-outline-primary mt-2 px-4"
                onClick={handleContactClick}
              >
                Contact Us
              </button>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default SubAssembliesSwitchgear;
